import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { DateTime } from "@tento-chrono";
import { SseClient } from "@tento-core/sse-client";
import { Field, Model, Scalar } from "@tento-core/model";
import { Json, WireDecode } from "@tento-lona/sheets";
import type {
  AgentStreamDelta,
  ChatContentBlock,
  ChatMessage,
  PipelineDebug,
} from "@tento-chat/model/chat-types";
import {
  CONTENT_BLOCK,
  PIPELINE_DEBUG,
  type ChatDebugEntry,
  ChatProvider,
  ChatSessionId,
  type ChatSessionStore,
  type ChatSession,
  type ChatSessionSummary,
} from "./chat-session-store.ts";
import type { ClientLifecycle } from "../../client/handle-errors.ts";

const STREAM_PATH = "/api/mock/chat/stream";
const SCHEMA_VERSION = 1 as const;

/** Wire body of the chat stream request (`POST STREAM_PATH`). */
type StreamRequestBody = {
  message: string;
  sessionId: ChatSessionId;
  provider: ChatProvider;
  model: string;
  timezone?: Option<string>;
};

/** The options of one `ChatAgentSession.send`: the viewer's IANA timezone for wall-clock-aware tools. */
export type ChatSendOptions = {
  /** IANA timezone string passed to the backend for wallclock-aware tools (`null`: none). */
  timezone: Option<string>;
};

/**
 * Listener for observing session state mutations. The UI layer wires its
 * virtualized `ChatThread` up to these callbacks; tests can inspect
 * `session.messages` / `session.debugEntries` directly and don't need to
 * subscribe.
 */
export type ChatSessionListener = {
  onDelta?(delta: AgentStreamDelta): void;
  onMessageAdded?(msg: ChatMessage): void;
  onMessageUpdated?(msg: ChatMessage): void;
  onDebugEntryAdded?(entry: ChatDebugEntry): void;
  onStreamEnd?(messageId: string, reason: "complete" | "interrupted" | "error"): void;
};

/** Types that go with `ChatsClient`: `Deps`, `NewSessionOptions`. */
export namespace ChatsClient {
  export type Deps = {
    baseUrl: string;
    accessToken: Option<string>;
    persistence: ChatSessionStore;
    /**
     * The transport the chat stream goes through (the client's
     * `ClientConfig.fetch`); `null` uses the global `fetch` at send time.
     */
    fetch: Option<typeof fetch>;
    /**
     * The owning client's liveness (`LonaSdk.Client.Lifecycle`): after its
     * `dispose`, a session's `send` — also on a session or a client taken
     * before — rejects with `LonaSdk.Client.DisposedError`, nothing
     * appended or sent.
     */
    lifecycle: ClientLifecycle;
  };
  /**
   * What `newSession` starts: its id (`null`: a fresh one,
   * `ChatSessionId.mint()`), and the provider and model it talks to.
   */
  export type NewSessionOptions = {
    readonly id: Option<ChatSessionId>;
    readonly provider: ChatProvider;
    readonly model: string;
  };
}

/**
 * Entry point for chat agent interactions. Hands out `ChatAgentSession`
 * instances, each of which owns its own SSE connection, message list,
 * and debug-entry log — and writes all of it through to
 * `ChatSessionStore`.
 */
export class ChatsClient {
  readonly #deps: ChatsClient.Deps;

  constructor(deps: ChatsClient.Deps) {
    this.#deps = deps;
  }

  /** Start a session with `options`' provider and model (`options.id` `null`: a fresh id). */
  newSession(options: ChatsClient.NewSessionOptions): ChatAgentSession {
    const id = options.id ?? ChatSessionId.mint();
    const now = Date.now();
    const session: ChatSession = {
      schemaVersion: SCHEMA_VERSION,
      id,
      title: null,
      createdAt: now,
      updatedAt: now,
      provider: options.provider,
      model: options.model,
      messages: [],
      debugEntries: [],
    };
    return new ChatAgentSession(session, this.#deps);
  }

  /** Hydrate a persisted session. Returns null if not found. */
  resumeSession(id: ChatSessionId): Option<ChatAgentSession> {
    const session = this.#deps.persistence.peek(id);
    if (!session) return null;
    this.#deps.persistence.setLastActiveSessionId(id);
    return new ChatAgentSession(session, this.#deps);
  }

  /** Most-recent-first session summaries; `null` limit lists them all. */
  peekSessions(limit: Option<number>): ChatSessionSummary[] {
    return this.#deps.persistence.peekSessions(limit);
  }

  deleteSession(id: ChatSessionId): void {
    this.#deps.persistence.delete(id);
  }
}

/**
 * Stateful owner of one conversation — messages, debug entries, model
 * selection — plus the SSE connection while streaming. All mutations
 * route through `apply*` helpers so listeners and persistence stay in
 * sync.
 */
export class ChatAgentSession {
  #session: ChatSession;
  readonly #deps: ChatsClient.Deps;
  #listeners: ChatSessionListener[] = [];
  #connection: Option<SseClient.Connection> = null;
  #streamingMessageId: Option<string> = null;

  constructor(session: ChatSession, deps: ChatsClient.Deps) {
    this.#session = session;
    this.#deps = deps;
  }

  // ── Read API ─────────────────────────────────────────────────────

  get id(): ChatSessionId {
    return this.#session.id;
  }

  get messages(): readonly ChatMessage[] {
    return this.#session.messages;
  }

  get debugEntries(): readonly ChatDebugEntry[] {
    return this.#session.debugEntries;
  }

  get provider(): ChatProvider {
    return this.#session.provider;
  }

  get model(): string {
    return this.#session.model;
  }

  get title(): Option<string> {
    return this.#session.title;
  }

  // ── Subscription ─────────────────────────────────────────────────

  /** Call `listener`'s hooks on every session change (deltas, messages, status); returns its unsubscribe. */
  onChange(listener: ChatSessionListener): Unsubscribe {
    this.#listeners.push(listener);
    return () => {
      this.#listeners = this.#listeners.filter((l) => l !== listener);
    };
  }

  // ── Send / stream ────────────────────────────────────────────────

  /**
   * Send a user message and stream the assistant's response. Resolves
   * when the stream closes (either via `message_stop` or an interrupt).
   * Caller can await completion for a deterministic finish signal.
   */
  send(text: string, opts: Option<ChatSendOptions>): Promise<void> {
    try {
      this.#deps.lifecycle.assertLive("chats.send");
    } catch (err) {
      return Promise.reject(err);
    }
    // 1. Append the user message and seed a streaming assistant message.
    const userMsg: ChatMessage = {
      id: makeMessageId("user"),
      role: "user",
      content: [{ type: "text", text }],
      isStreaming: false,
      createdAt: Date.now(),
    };
    this.#appendMessage(userMsg);
    if (this.#session.title == null) {
      this.#session.title = deriveTitle(text);
    }

    const assistantId = makeMessageId("assistant");
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: [{ type: "loading" }],
      isStreaming: true,
      createdAt: Date.now(),
      streamStartedAt: DateTime.utc.now(),
    };
    this.#appendMessage(assistantMsg);
    this.#streamingMessageId = assistantId;
    this.#persist();

    // 2. Open the SSE stream.
    return new Promise((resolve) => {
      const body: StreamRequestBody = {
        message: text,
        sessionId: this.#session.id,
        provider: this.#session.provider,
        model: this.#session.model,
      };
      if (opts?.timezone != null) body.timezone = opts.timezone;

      this.#connection?.abort();
      this.#connection = SseClient.stream(
        joinUrl(this.#deps.baseUrl, STREAM_PATH),
        body,
        {
          onEvent: (event) => this.#handleEvent(event, assistantId),
          onClose: () => {
            const finished = this.#streamingMessageId;
            this.#streamingMessageId = null;
            this.#connection = null;
            if (finished) this.#finalizeIfStreaming(finished, "complete");
            resolve();
          },
          onError: (err) => {
            Tracing.log("chat-agent/stream-error", { error: String(err) });
            const finished = this.#streamingMessageId;
            this.#streamingMessageId = null;
            this.#connection = null;
            if (finished) this.#finalizeIfStreaming(finished, "error");
            resolve();
          },
        },
        this.#deps.accessToken != null
          ? { Authorization: `Bearer ${this.#deps.accessToken}` }
          : undefined,
        sseTransport(this.#deps.fetch),
      );
    });
  }

  interrupt(): void {
    if (this.#streamingMessageId) {
      const id = this.#streamingMessageId;
      this.#replaceMessage(id, (m) => ({
        ...m,
        content: [{ type: "interrupted" } as ChatContentBlock],
        isStreaming: false,
        streamEndedAt: DateTime.utc.now(),
      }));
      this.#notifyStreamEnd(id, "interrupted");
    }
    this.#streamingMessageId = null;
    this.#connection?.abort();
    this.#connection = null;
    this.#persist();
  }

  // ── Internal delta application ───────────────────────────────────

  #handleEvent(event: SseClient.Event, assistantId: string): void {
    let wire: WireStreamDelta;
    try {
      wire = WireDecode.parse(
        "AgentStreamDelta",
        WIRE_STREAM_DELTA,
        Json.wire(JSON.parse(event.data)) ?? null,
      );
    } catch (err) {
      Tracing.log("chat-agent/bad-sse-payload", { error: String(err) });
      return;
    }
    // Server sends deltas without messageId — tag with the current
    // assistant turn so downstream lookups (replaceMessage, applyDelta) work.
    const delta: AgentStreamDelta = wire.type === "pipeline_debug"
      ? wire
      : { ...wire, messageId: assistantId };

    for (const listener of this.#listeners) listener.onDelta?.(delta);

    switch (delta.type) {
      case "pipeline_debug": {
        const entry: ChatDebugEntry = { messageId: assistantId, debug: delta };
        this.#session.debugEntries.push(entry);
        for (const listener of this.#listeners) {
          listener.onDebugEntryAdded?.(entry);
        }
        this.#persist();
        break;
      }
      case "content_block_start": {
        this.#replaceMessage(delta.messageId, (m) => {
          const content = [...m.content];
          // Replace a leading "loading" placeholder on the first real block.
          if (
            delta.blockIndex === 0 &&
            content.length === 1 &&
            content[0]?.type === "loading"
          ) {
            content[0] = delta.block;
          } else {
            content[delta.blockIndex] = delta.block;
          }
          return { ...m, content };
        });
        this.#persist();
        break;
      }
      case "text_delta": {
        this.#replaceMessage(delta.messageId, (m) => {
          const content = [...m.content];
          const existing = content[delta.blockIndex];
          if (existing && existing.type === "text") {
            content[delta.blockIndex] = {
              type: "text",
              text: existing.text + delta.delta,
            };
          } else {
            content[delta.blockIndex] = { type: "text", text: delta.delta };
          }
          return { ...m, content };
        });
        break;
      }
      case "content_block_stop":
        // No-op on the session (the UI uses this to finalize rendering).
        break;
      case "message_stop":
        this.#finalizeIfStreaming(delta.messageId, "complete");
        break;
      case "tool_use":
        // Tool actions are app-layer concerns. The UI handles them via
        // the onDelta listener; the session state doesn't change here.
        break;
    }
  }

  #finalizeIfStreaming(
    messageId: string,
    reason: "complete" | "interrupted" | "error",
  ): void {
    let changed = false;
    this.#replaceMessage(messageId, (m) => {
      if (!m.isStreaming) return m;
      changed = true;
      return {
        ...m,
        isStreaming: false,
        streamEndedAt: DateTime.utc.now(),
        ...(reason === "error" && m.content.every((b) => b.type === "loading")
          ? { content: [{ type: "error", message: "Stream error" }] as ChatContentBlock[] }
          : {}),
      };
    });
    if (changed) {
      this.#notifyStreamEnd(messageId, reason);
      this.#persist();
    }
  }

  #notifyStreamEnd(
    messageId: string,
    reason: "complete" | "interrupted" | "error",
  ): void {
    for (const listener of this.#listeners) {
      listener.onStreamEnd?.(messageId, reason);
    }
  }

  // ── Mutation helpers ─────────────────────────────────────────────

  #appendMessage(msg: ChatMessage): void {
    this.#session.messages.push(msg);
    for (const listener of this.#listeners) listener.onMessageAdded?.(msg);
  }

  #replaceMessage(
    id: string,
    update: (m: ChatMessage) => ChatMessage,
  ): void {
    const idx = this.#session.messages.findIndex((m) => m.id === id);
    const current = this.#session.messages[idx];
    if (current == null) return;
    const next = update(current);
    this.#session.messages[idx] = next;
    for (const listener of this.#listeners) listener.onMessageUpdated?.(next);
  }

  #persist(): void {
    if (this.#session.messages.length === 0) return;
    this.#session.updatedAt = Date.now();
    this.#deps.persistence.save(this.#session);
    this.#deps.persistence.setLastActiveSessionId(this.#session.id);
  }
}

// ── Helpers ────────────────────────────────────────────────────────

function makeMessageId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function deriveTitle(firstUserMessage: string): string {
  const trimmed = firstUserMessage.trim().replace(/\s+/g, " ");
  return trimmed.length > 60 ? trimmed.slice(0, 60) + "…" : trimmed;
}

function joinUrl(base: string, path: string): string {
  if (base.endsWith("/") && path.startsWith("/")) return base + path.slice(1);
  if (!base.endsWith("/") && !path.startsWith("/")) return `${base}/${path}`;
  return base + path;
}

/**
 * `fetchFn` as the SSE client's transport (`null`: the global `fetch`). The
 * stream's abort signal is the platform's `AbortSignal` at runtime; the
 * `instanceof` check narrows it for `RequestInit` without a cast.
 */
function sseTransport(fetchFn: Option<typeof fetch>): Option<SseClient.Transport> {
  if (fetchFn == null) return null;
  return (url, init) =>
    fetchFn(url, {
      method: init.method,
      headers: init.headers,
      body: init.body,
      signal: init.signal instanceof AbortSignal ? init.signal : null,
    });
}

// ── Stream delta decoder ─────────────────────────────────────────────

/**
 * A stream delta as the server sends it: an `AgentStreamDelta` whose
 * turn-scoped variants carry no `messageId` yet (`#handleEvent` tags them
 * with the assistant turn they stream into).
 */
type WireStreamDelta =
  | PipelineDebug
  | DistributiveOmit<Exclude<AgentStreamDelta, PipelineDebug>, "messageId">;

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

const DELTA_TYPE: Model<WireStreamDelta["type"]> = Scalar.stringUnion<WireStreamDelta["type"]>([
  "pipeline_debug",
  "tool_use",
  "content_block_start",
  "text_delta",
  "content_block_stop",
  "message_stop",
]);

/** One decoder per delta `type`: the envelope's tag picks the variant. */
const DELTA_VARIANTS: { readonly [K in WireStreamDelta["type"]]: Model<WireStreamDelta> } = {
  pipeline_debug: PIPELINE_DEBUG,
  tool_use: Model.object<WireStreamDelta>({
    type: Field.required(DELTA_TYPE),
    toolIndex: Field.required(Scalar.integer),
    action: Field.optional(Scalar.json),
  }),
  content_block_start: Model.object<WireStreamDelta>({
    type: Field.required(DELTA_TYPE),
    blockIndex: Field.required(Scalar.integer),
    block: Field.required(CONTENT_BLOCK),
  }),
  text_delta: Model.object<WireStreamDelta>({
    type: Field.required(DELTA_TYPE),
    blockIndex: Field.required(Scalar.integer),
    delta: Field.required(Scalar.string),
  }),
  content_block_stop: Model.object<WireStreamDelta>({
    type: Field.required(DELTA_TYPE),
    blockIndex: Field.required(Scalar.integer),
  }),
  message_stop: Model.object<WireStreamDelta>({
    type: Field.required(DELTA_TYPE),
  }),
};

/** What every stream delta's envelope holds: its variant tag. */
interface DeltaEnvelope {
  readonly type: WireStreamDelta["type"];
}

/**
 * A stream delta off the SSE wire: the `type` tag is checked against the
 * known variants, then the payload decodes through that variant's model (a
 * `content_block_start` block through the stored-session `CONTENT_BLOCK`, a
 * `pipeline_debug` record through `PIPELINE_DEBUG`).
 */
const WIRE_STREAM_DELTA: Model<WireStreamDelta> = Scalar.checked<DeltaEnvelope, WireStreamDelta>(
  "AgentStreamDelta",
  Model.object<DeltaEnvelope>({ type: Field.required(DELTA_TYPE) }),
  (envelope, raw) => WireDecode.parse(envelope.type, DELTA_VARIANTS[envelope.type], raw),
  null,
);
